import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { FormattedMessage } from 'react-intl';

import { boardSwitcherSharedState } from '@trello/board-switcher';
import { StarIcon } from '@trello/nachos/icons/star';
import { useSharedStateSelector } from '@trello/shared-state';

import { BoardSwitcherTiles } from './BoardSwitcherTiles';
import { DraggableStarredBoard } from './DraggableStarredBoard';
import { ExpandSection } from './ExpandSection';
import { starredBoardsState } from './starredBoardsState';
import { useBoardSwitcher } from './useBoardSwitcher';

export const StarredBoards: FunctionComponent = () => {
  const { starredBoards: boardStarsFromCache } = useBoardSwitcher();
  const [previousBoardStarsFromCache, setPreviousBoardStarsFromCache] =
    useState(boardStarsFromCache);

  useEffect(() => {
    starredBoardsState.setValue({
      starredBoards: boardStarsFromCache,
    });
  }, [boardStarsFromCache]);

  const boardStars = useSharedStateSelector(
    starredBoardsState,
    useCallback((state) => {
      return state.starredBoards;
    }, []),
  );
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );

  useEffect(() => {
    const cacheUpdate = !isEqual(
      boardStarsFromCache,
      previousBoardStarsFromCache,
    );
    if (cacheUpdate) {
      setPreviousBoardStarsFromCache(boardStarsFromCache);
      starredBoardsState.setValue({
        starredBoards: boardStarsFromCache,
      });
    }
  }, [boardStarsFromCache, previousBoardStarsFromCache]);

  const sortedBoardStars = useMemo(() => {
    return boardStars.slice().sort((a, b) => a.pos - b.pos);
  }, [boardStars]);

  if (sortedBoardStars.length === 0) {
    return null;
  }

  return (
    <ExpandSection
      id="starred"
      title={
        <FormattedMessage
          id="templates.board_switcher.starred"
          defaultMessage="Starred"
          description="Title of starred workspaces section on the board switcher"
        />
      }
      customIcon={<StarIcon />}
      isExpandedByDefault
      testId="board-switcher-starred-boards"
    >
      <BoardSwitcherTiles>
        {sortedBoardStars.map(({ id, idBoard }) => (
          <DraggableStarredBoard key={id} boardId={idBoard} layout={layout} />
        ))}
      </BoardSwitcherTiles>
    </ExpandSection>
  );
};
